/** QuickHouse — Mesure linéaire 3D : polyligne de points cliqués sur les surfaces
 *  (murs, sols, objets…). Chaque segment est droit ; longueurs par segment + total. */
import * as THREE from 'three';
import { useMemo } from 'react';
import { Html } from '@react-three/drei';
import { useProjectStore } from '../store/useProjectStore.js';

const ACCENT = '#3b9eff';

export default function Measure3D() {
  const points = useProjectStore((s) => s.measurePoints);

  const lineGeometry = useMemo(() => {
    if (points.length < 2) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(
      points.flatMap((p) => [p.x, p.y, p.z]), 3,
    ));
    return g;
  }, [points]);

  const segments = useMemo(() => {
    const out = [];
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      const len = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
      out.push({ mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 }, len });
    }
    return out;
  }, [points]);

  if (!points.length) return null;
  const total = segments.reduce((s, x) => s + x.len, 0);
  const last = points[points.length - 1];

  return (
    <group>
      {points.map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]} renderOrder={10} userData={{ noRay: true }}>
          <sphereGeometry args={[0.035, 12, 12]} />
          <meshBasicMaterial color={ACCENT} depthTest={false} />
        </mesh>
      ))}
      {lineGeometry && (
        <line geometry={lineGeometry} renderOrder={10} userData={{ noRay: true }}>
          <lineBasicMaterial color={ACCENT} depthTest={false} />
        </line>
      )}
      {segments.map((s, i) => (
        <Html key={i} position={[s.mid.x, s.mid.y + 0.06, s.mid.z]} center style={{ pointerEvents: 'none' }}>
          <div style={{
            background: 'rgba(12,14,20,0.85)', color: '#3b9eff', padding: '1px 5px',
            borderRadius: 3, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap',
          }}>
            {Math.round(s.len * 100)} cm
          </div>
        </Html>
      ))}
      {segments.length > 1 && (
        <Html position={[last.x, last.y + 0.22, last.z]} center style={{ pointerEvents: 'none' }}>
          <div style={{
            background: 'rgba(59,158,255,0.92)', color: '#fff', padding: '2px 7px',
            borderRadius: 3, fontSize: 12, fontWeight: 600,
            fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap',
          }}>
            Σ {(total).toFixed(2)} m
          </div>
        </Html>
      )}
    </group>
  );
}